import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { api, queries, unwrap } from "@/api";
import { useAdminView } from "@/adminView";
import { ErrorNote } from "@/components/ErrorNote";
import { eventSummary, formatDateTime } from "@/lib/format";

/** A return's kind in words (decision 064). */
function kindLabel(kind: string): string {
  switch (kind) {
    case "publish":
      return "shared";
    case "commit":
      return "returned";
    case "rollback":
      return "restored";
    case "conflict_accept":
      return "held return accepted";
    default:
      return kind.replace(/_/g, " ");
  }
}

export const Route = createFileRoute("/airframes/$id/history")({
  component: History,
});

/**
 * The airframe's history (F478, decision 099): the returns, who returned it
 * when and what was restored, with the download and the restore for the
 * owner, and the timeline under them. For the members; the logbook holds
 * the flights alone.
 */
function History() {
  const { id } = Route.useParams();
  const airframe = useQuery(queries.airframe(id));
  const adminView = useAdminView();
  const canRead = !!airframe.data?.your_role || adminView;
  const events = useQuery({ ...queries.events(id), enabled: canRead });
  const versions = useQuery({ ...queries.versions(id), enabled: canRead });
  const queryClient = useQueryClient();
  const manages = airframe.data?.your_role === "owner" || airframe.data?.your_role === "captain";
  const newestSeq = versions.data?.items.reduce((m, v) => Math.max(m, v.seq), 0) ?? 0;
  // The restore (F202, decision 061): an earlier version becomes the newest
  // again, as a new version that names its package; refused while anyone
  // has the aircraft. Asked twice: a restore replaces what the next booking
  // installs.
  const [restoring, setRestoring] = useState<string | null>(null);
  const restore = useMutation({
    mutationFn: async (vid: string) =>
      unwrap(await api.POST("/api/v1/airframes/{id}/versions/{target}", { params: { path: { id, target: `${vid}:rollback` } } })),
    onSuccess: () => {
      setRestoring(null);
      queryClient.invalidateQueries({ queryKey: ["versions", id] });
      queryClient.invalidateQueries({ queryKey: ["events", id] });
      queryClient.invalidateQueries({ queryKey: ["airframe", id] });
      queryClient.invalidateQueries({ queryKey: ["airframes"] });
    },
  });
  // The tab is opened in the click itself (popup blockers allow that) and
  // pointed at the presigned URL once the API answered.
  const download = useMutation({
    mutationFn: async ({ vid, tab }: { vid: string; tab: Window | null }) => {
      try {
        const d = unwrap(await api.GET("/api/v1/versions/{vid}/download-url", { params: { path: { vid } } }));
        if (tab) tab.location.href = d.url;
        else window.location.href = d.url;
        return d;
      } catch (e) {
        tab?.close();
        throw e;
      }
    },
  });
  if (airframe.data && !canRead) return <p className="muted text-sm">The history is for the members of the airframe.</p>;
  return (
    <div className="space-y-6" data-testid="history-page">
      <section className="card-flush">
        <div className="card-head">
          <h2 className="text-[1.0714rem] font-semibold">Returns</h2>
          <span className="muted text-xs tnum">{versions.data?.total ?? 0} entries</span>
        </div>
        <ErrorNote error={versions.error ?? download.error ?? restore.error} />
        {restore.isSuccess ? (
          <p className="p-4 text-sm text-ok" data-testid="restored">
            Entry {restore.data.restores_seq} is the newest again, as entry {restore.data.seq}: the next pilot to book the aircraft gets it.
          </p>
        ) : null}
        {versions.data && versions.data.items.length === 0 ? <p className="muted p-5 text-sm">No return yet.</p> : null}
        {versions.data && versions.data.items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table" data-testid="versions-table">
              <thead>
                <tr>
                  <th>Entry</th>
                  <th>What</th>
                  <th>When</th>
                  <th>By</th>
                  <th>CL650</th>
                  <th>Package</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {versions.data.items.map((v) => (
                  <tr key={v.id} data-seq={v.seq}>
                    <td className="tnum">{v.seq}</td>
                    <td>
                      {kindLabel(v.kind)}
                      {v.restores_seq ? <span className="muted"> of entry {v.restores_seq}</span> : null}
                    </td>
                    <td className="tnum">{formatDateTime(v.created_at)}</td>
                    <td>{v.created_by?.name ?? <span className="muted">removed pilot</span>}</td>
                    <td>{v.cl650_version}</td>
                    <td className="tnum">{v.bytes ? `${(v.bytes / (1024 * 1024)).toFixed(1)} MB` : <span className="muted">metadata only</span>}</td>
                    <td>
                      <span className="flex flex-wrap items-center justify-end gap-2">
                        {v.has_package && airframe.data?.your_role ? (
                          <button
                            type="button"
                            className="btn"
                            onClick={() => download.mutate({ vid: v.id, tab: window.open("about:blank", "_blank") })}
                            disabled={download.isPending}
                            data-testid="download-version"
                          >
                            Download
                          </button>
                        ) : null}
                        {manages && v.seq < newestSeq ? (
                          restoring === v.id ? (
                            <>
                              <span className="muted text-xs">Make entry {v.seq} the newest? Nobody may have the aircraft.</span>
                              <button type="button" className="btn btn-primary" onClick={() => restore.mutate(v.id)} disabled={restore.isPending} data-testid="confirm-restore">
                                Restore
                              </button>
                              <button type="button" className="btn" onClick={() => setRestoring(null)} disabled={restore.isPending}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="btn"
                              onClick={() => setRestoring(v.id)}
                              disabled={restore.isPending}
                              data-testid="restore-version"
                              title="Make this entry the newest again; the next pilot to book the aircraft gets it"
                            >
                              Restore
                            </button>
                          )
                        ) : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
      <section className="card-flush">
        <div className="card-head">
          <h2 className="text-[1.0714rem] font-semibold">Timeline</h2>
          <span className="muted text-xs tnum">{events.data?.total ?? 0} events</span>
        </div>
        <ErrorNote error={events.error} />
        <ul className="space-y-1 p-4 text-sm" data-testid="timeline">
          {events.data?.items.map((e) => (
            <li key={e.id} className="flex gap-3">
              <span className="muted tnum shrink-0">{formatDateTime(e.created_at)}</span>
              <span className={e.type === "version.refused" ? "text-warn" : "muted"}>{eventSummary(e)}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
